import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { SearchIndividualResult } from "@/Common/Api/Individuals/Dto/SearchIndividualResult";
import { FindPatientDuplicateRequest } from "@/Common/Api/Patient/Dto/FindPatientDuplicateRequest";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { PatientEdit, PatientEditState } from "@/Common/Components/Patient/PatientEdit";
import { PatientAdvancedSearchControl } from "@/Common/Components/Search/PatientAdvancedSearchControl";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IImportablePatient } from "@/Common/Models/Patient/IPatient";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ButtonImageControl } from "../Buttons/ButtonImageControl";
import { DuplicatePatientWarningPopup } from "./DuplicatePatientWarningPopup";
import { PatientImportHandler } from "./PatientImportHandler";
import { PatientImportValidationPopup } from "./PatientImportValidationFailurePopup";
import { PatientImportValidationResult } from "./PatientImportValidationResult";
import { TransferablePatientInfoComponent } from "./TransferablePatientInfoComponent";

import img_icon_create_patient from "@/Globals/_images/icons/sidebar/create-patient.png";

interface IPatientImportProps {
    componentId: string;
    patient: IImportablePatient;
    handler: PatientImportHandler;
    onClose: () => void;
    onLink: (existingPatientContactFacetId: string, searchText?: string, includeDeactivatedPatients?: boolean) => void;
    onCreate: (patientCreationModel: IPatientCreationModel) => void;
    onErrorOkClick?: () => void;
    createLabel: string;
    linkLabel: string;

    duplicateCheckOnLinkEnabled: boolean;
    patientValidationRules?: ((importedPatientInfo: IImportablePatient, selectedPatient: Person) => PatientImportValidationResult)[];

    searchText?: string;
    patientCreationModel?: IPatientCreationModel;
    includeDeactivatedPatients?: boolean;

    enablePatientCreation: boolean;

    top?: number;
    width?: number;
}

enum PatientImportActionType {
    LINK,
    CREATE
}

class PatientImportComponentState {
    public searchText: string;
    public includeDeactivatedPatients: boolean;
    public selectedPerson: Person;
    public isPatientCreationMode: boolean;
    public isFormValid: boolean;
    public isImporting: boolean;

    public duplicatePatients: Patient[];
    public failedPatientImportValidationResults: PatientImportValidationResult[];
    public actionType: PatientImportActionType;

    public patientCreationModel: IPatientCreationModel;

    public latestHealthCardNumberCheckedForDuplicate: string;
}

class PatientImportComponent extends BaseReactComponent<IPatientImportProps, PatientImportComponentState> {
    constructor(props: IPatientImportProps) {
        super(props);

        const initialState = new PatientImportComponentState();
        initialState.searchText = props.searchText ?? this.getDefaultPatientImportSearchText();
        initialState.isPatientCreationMode = props.patientCreationModel != null;
        initialState.includeDeactivatedPatients = props.includeDeactivatedPatients ?? false;
        initialState.patientCreationModel = props.patientCreationModel ?? Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel());
        initialState.isFormValid = false;
        initialState.isImporting = false;
        initialState.latestHealthCardNumberCheckedForDuplicate = "";
        initialState.duplicatePatients = [];
        initialState.failedPatientImportValidationResults = [];

        this.state = initialState;
    }

    public componentDidMount(): void {
        this.setOverlay(this.props.componentId);
    }

    public render(): JSX.Element {
        return (
            <div>
                {!isArrayNullOrUndefinedOrEmpty(this.state.duplicatePatients) && this.renderDuplicateWarning(this.state.duplicatePatients)}
                {!isArrayNullOrUndefinedOrEmpty(this.state.failedPatientImportValidationResults) && this.renderPatientImportValidationWarning()}

                <Popup
                    Id={this.props.componentId}
                    title={cacheStore.GetString("PortalPatientSearchAttachFileLabel")}
                    width={this.width}
                    topPosition={this.top}
                    draggable={false}
                    centeredHorizontally
                    onCloseDelegate={() => this.onClose()}
                >
                    <div>
                        <div className="flex-container">
                            <div style={{ width: "43%", padding: "20px" }}>
                                <TransferablePatientInfoComponent
                                    patient={this.props.patient.toTransferable()}
                                    onPatientInfoClick={(txt: string) => this.mutateState(s => s.searchText = txt)}
                                />
                            </div>
                            <div style={{ borderLeft: "solid 1px #ccc", width: "57%", maxHeight: "600px", overflowY: "auto" }}>
                                {this.renderPatientSearchView()}
                                {this.renderPatientCreationView()}
                            </div>
                        </div>
                    </div>
                    {this.renderFooter()}
                </Popup>
            </div>
        );
    }

    private renderPatientSearchView(): JSX.Element {
        if (this.state.isPatientCreationMode)
            return null;

        return (
            <div id="patientSearchView">
                <div className="flex-container">
                    <div style={{ padding: "11px 10px", width: "100%" }}>
                        <TextboxControl
                            type="text"
                            value={this.state.searchText}
                            onChange={(txt: string) => this.mutateState(s => s.searchText = txt)}
                            placeholder={cacheStore.GetString("SearchLabel")}
                        />

                        <div className="portal-patients-results" style={{ overflowY: "auto" }}>
                            <div className="checkbox" style={{ float: "left" }}>
                                <label>
                                    <input
                                        checked={this.state.includeDeactivatedPatients}
                                        type="checkbox"
                                        onChange={() => this.mutateState(s => s.includeDeactivatedPatients = !s.includeDeactivatedPatients)}
                                    />
                                    {cacheStore.GetString("DeactivatedFilesLabel")}
                                </label>
                            </div>
                            <div style={{ float: "right" }}>
                                {this.props.enablePatientCreation && (
                                    <ButtonImageControl
                                        id="createPatient"
                                        style={{ height: "50px" }}
                                        onClick={() => this.handleOnCreatePatientClick()}
                                        imagePath={img_icon_create_patient}
                                    />
                                )}
                            </div>
                        </div>
                        <PatientAdvancedSearchControl
                            componentId="internalpatientsearch"
                            defaultTextValue={this.state.searchText}
                            maxResultsToReturn={8}
                            onPatientSelectCallback={selectedPerson => this.mutateState(s => s.selectedPerson = selectedPerson)}
                            isIncludingInactivePatients={this.state.includeDeactivatedPatients}
                            customMylePatientRenderFunction={(person, patientDivClassName, index, selectFunction) => this.props.handler.renderAdvancePatientSearch(person, this.props.patient, patientDivClassName, index, selectFunction)}
                            isFocusOnLoad={true}
                            isRenderTextbox={false}
                            isScrollBarActive={false}
                            isClickOnResultDisabled={false}
                            isAutoResize={true}
                            isSearchingPortal={false}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private renderPatientCreationView(): JSX.Element {
        if (!this.state.isPatientCreationMode)
            return null;

        return (
            <div className="patientEdit-scroll" style={{ width: "100%" }}>
                <h4>{cacheStore.GetString("CreatePatientLabel")}</h4>
                <PatientEdit
                    id="PatientCreationEditImport"
                    patientModel={this.state.patientCreationModel}
                    stateUpdateFunction={updatedPatient => this.onPatientEditStateUpdate(updatedPatient)}
                    hideFooter
                    disabled={false}
                    duplicatePromptLabelName=""
                    cssClass="patient-import-popup"
                    duplicatePopupCSS="adjust-margin"
                    shouldNotValidateDuplicate
                    onValidHealthCardInfo={patientCreationModel => this.handleValidHealthCardInfo(patientCreationModel)}
                />
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return this.state.isPatientCreationMode
            ? this.renderPatientCreationFooter()
            : this.renderPatientSearchFooter();
    }

    private renderPatientSearchFooter(): JSX.Element {
        return (
            <div className="p-footer flex-display flex-end flex-gap-xs">
                <ButtonControl
                    onClick={() => this.onClose()}
                    className="btn btn-default"
                    label={cacheStore.GetString("CancelLabel")}
                />
                <ButtonControl
                    onClick={() => this.mutateState(
                        s => s.isImporting = true,
                        () => this.handleOnLink()
                    )}
                    disabled={!this.isImportable || this.state.isImporting || !this.state.selectedPerson}
                    variant="brand"
                    label={cacheStore.GetString(this.props.linkLabel)}
                />
            </div>
        );
    }

    private renderPatientCreationFooter(): JSX.Element {
        return (
            <div className="p-footer flex-display flex-end flex-gap-xs">
                <ButtonControl
                    onClick={() => this.mutateState(s => s.isPatientCreationMode = false)}
                    className="btn btn-default"
                    label={cacheStore.GetString("BackLabel")}
                />
                <ButtonControl
                    onClick={() => this.mutateState(
                        s => s.isImporting = true,
                        () => this.triggerCreate()
                    )}
                    variant="brand"
                    label={cacheStore.GetString(this.props.createLabel)}
                    disabled={!this.state.isFormValid || this.state.isImporting}
                />
            </div>
        );
    }

    private renderDuplicateWarning(patients: Patient[]): JSX.Element {
        return (
            <DuplicatePatientWarningPopup
                existingPatients={patients}
                onContinue={() => this.handleOnContinueDuplicateWarning(this.state.actionType)}
                onLink={patient => this.handleOnLinkDuplicateWarning(patient)}
                onClose={() => this.mutateState(s => {
                    s.duplicatePatients = [];
                    s.actionType = null;
                    s.isImporting = false;
                })}
            />
        );
    }

    private renderPatientImportValidationWarning(): JSX.Element {
        return (
            <PatientImportValidationPopup
                onLink={() => this.triggerLink(this.state.selectedPerson.ContactFacetId)}
                onClose={() => this.mutateState(s => {
                    s.failedPatientImportValidationResults = [];
                    s.actionType = null;
                    s.isImporting = false;
                })}
                patientToImport={this.props.patient}
                selectedPerson={this.state.selectedPerson}
                failedPatientImportValidationResults={this.state.failedPatientImportValidationResults}
            />
        );
    }

    private handleOnContinueDuplicateWarning(actionType: PatientImportActionType): void {
        this.mutateState(
            s => {
                s.duplicatePatients = [];
                s.actionType = null;
            },
            () => {
                switch (actionType) {
                    case PatientImportActionType.LINK:
                        this.triggerLink(this.state.selectedPerson.ContactFacetId);
                        break;
                    case PatientImportActionType.CREATE:
                        this.showPatientCreationView();
                        break;
                }
            }
        );
    }

    private handleOnLinkDuplicateWarning(patient: Patient): void {
        this.mutateState(
            s => s.selectedPerson = Person.FromPatient(patient),
            () => this.triggerLink(this.state.selectedPerson.ContactFacetId)
        );
    }

    private get isImportable(): boolean {
        return this.props.handler.isImportable(this.props.patient, this.state.selectedPerson?.ContactFacetId);
    }

    private get width(): number {
        return this.props.width || 1000;
    }

    private get top(): number {
        return this.props.top || 30;
    }

    private async handleOnLink(): Promise<void> {
        try {
            if (this.props.duplicateCheckOnLinkEnabled) {
                const duplicates =
                    (await this.getDuplicatePatientsFromExternalPatientInfo() ?? [])
                        .filter(p => p.PatientResponse.ContactFacet_id != this.state.selectedPerson?.ContactFacetId);

                if (duplicates.any()) {
                    this.mutateState(s => {
                        s.duplicatePatients = duplicates;
                        s.actionType = PatientImportActionType.LINK;
                    });
                    return;
                }
            }

            const failedValidationResults = (this.props.patientValidationRules ?? [])
                .map(validationRule => validationRule(this.props.patient, this.state.selectedPerson))
                .filter(validationResult => !validationResult.isValid);
            if (failedValidationResults.any()) {
                this.mutateState(s => {
                    s.failedPatientImportValidationResults = failedValidationResults;
                    s.actionType = PatientImportActionType.LINK;
                });
                return;
            }

            this.triggerLink(this.state.selectedPerson.ContactFacetId);
        }
        catch (error) {
            this.handleError(error);
        }
    }

    private triggerLink(contactFacetId: string): void {
        this.props.onLink && this.props.onLink(contactFacetId, this.state.searchText, this.state.includeDeactivatedPatients);
    }

    private async handleOnCreatePatientClick(): Promise<void> {
        let duplicates = await this.getDuplicatePatientsFromExternalPatientInfo();

        if (!isArrayNullOrUndefinedOrEmpty(duplicates)) {
            this.mutateState(s => {
                s.duplicatePatients = duplicates;
                s.actionType = PatientImportActionType.CREATE;
                s.latestHealthCardNumberCheckedForDuplicate = this.props.patient.NAM;
            });
        }
        else {
            this.showPatientCreationView();
        }
    }

    private showPatientCreationView(): void {
        if (this.state.isPatientCreationMode)
            return;

        this.mutateState(s => {
            s.isPatientCreationMode = true;
            s.selectedPerson = null;
            s.patientCreationModel = this.props.patient.toPatientCreationModel();
        });
    }

    private triggerCreate(): void {
        this.props.onCreate && this.props.onCreate(this.state.patientCreationModel);
    }

    private async getDuplicatedPatients(duplicateRequest: FindPatientDuplicateRequest): Promise<Patient[]> {
        if (IsNullOrWhiteSpace(duplicateRequest.number))
            return [];

        let duplicatePatients = await this.props.handler.getDuplicatePatients(duplicateRequest);

        return await this.getPatientInfoFromPatientId(duplicatePatients);
    }

    private async getPatientInfoFromPatientId(duplicatePatients: SearchIndividualResult[]): Promise<Patient[]> {
        return Promise.all(
            duplicatePatients?.map(async p => {
                let response = await Client.postAsync<LoadPatientModel>(
                    ServerOperations.Server.Myle_LoadPatient,
                    {
                        IndividualId: p.ContactId,
                        Inactivable: p.ContactFacetId
                    }
                );

                return Patient.FromResponseData(response.data);
            })
        );
    }

    private async getDuplicatePatientsFromExternalPatientInfo(): Promise<Patient[]> {
        return await this.getDuplicatedPatients(new FindPatientDuplicateRequest({
            number: this.props.patient?.NAM,
            regionLookupId: this.props.patient.toPatientCreationModel().get("selectedHealthCardRegionLookupId")
        }));
    }

    private handleError(errors: ErrorMessage[]): void {
        if (isArrayNullOrUndefinedOrEmpty(errors))
            return;

        sessionStore.showPopup({
            content: errors,
            okAction: () => this.props.onErrorOkClick ? this.props.onErrorOkClick() : this.props.onClose()
        });
    }

    private getDefaultPatientImportSearchText(): string {
        let patient = this.props.patient;
        let searchText = "";

        if (!patient)
            return searchText;

        if (patient.NAM)
            return patient.NAM;

        if (!IsNullOrWhiteSpace(patient.FirstName) && !IsNullOrWhiteSpace(patient.LastName))
            return `${patient.LastName} ${patient.FirstName}`;

        return searchText;
    }

    private onClose(): void {
        this.props.onClose && this.props.onClose();
    }

    private onPatientEditStateUpdate(updatedPatient: Readonly<PatientEditState>): void {
        this.mutateState(s => {
            s.patientCreationModel = updatedPatient.patientModel;
            s.isFormValid = PatientEditState.IsValid(updatedPatient);
        });
    }

    private async handleValidHealthCardInfo(patientCreationModel: IPatientCreationModel): Promise<void> {
        let healthCardNumber = patientCreationModel.get("NAMEdit");
        if (!healthCardNumber || this.state.latestHealthCardNumberCheckedForDuplicate === healthCardNumber)
            return;

        let duplicates = await this.getDuplicatedPatients(
            new FindPatientDuplicateRequest({
                number: healthCardNumber,
                regionLookupId: patientCreationModel.get("selectedHealthCardRegionLookupId")
            })
        );

        if (!isArrayNullOrUndefinedOrEmpty(duplicates)) {
            this.mutateState(s => {
                s.duplicatePatients = duplicates;
                s.actionType = PatientImportActionType.CREATE;
                s.latestHealthCardNumberCheckedForDuplicate = healthCardNumber;
            });
        }
    }
}

export { PatientImportComponent };
